import { useState } from "react";
import { useAudienceInterests } from "../lib/use-audience-interests";
import { CategoryPicker } from "./CategoryPicker";
import { IconClose, IconPlus } from "./icons";

/**
 * Who receives this newsletter — the first decision, and the one everything
 * below depends on.
 *
 * It leads with the question rather than a feature name, because the honest
 * reaction to "Audience & personalization / no categories yet" is "why do I
 * need a category to write a newsletter at all?". The answer — that the
 * category is what decides both the recipients and the articles — has to come
 * before the control, not after it.
 */
export function AudiencePanel({
  categories, locked, onChange,
}: {
  categories: string[];
  locked?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [picking, setPicking] = useState(false);
  // One fetch for the panel and the picker it opens: the labels on the chips
  // and the rows in the picker have to be the same list.
  const catalogue = useAudienceInterests();
  const nameOf = (value: string) =>
    catalogue.choices.find((c) => c.value === value);
  const selected = categories.filter(Boolean);

  return (
    <section className="nl-audience" aria-label="Audience">
      <div className="nl-audience-head">
        <span className="nl-sectionnum" aria-hidden>1</span>
        <div>
          <h2 className="nl-audience-title">Who should receive this newsletter?</h2>
          <p className="nl-audience-lede">Subscribers who follow the categories you pick.</p>
        </div>
      </div>

      <div className="nl-audience-body">
        {selected.length > 0 && (
          <ul className="nl-chips">
            {selected.map((label) => {
              const cat = nameOf(label);
              return (
                <li key={label} className="nl-chip">
                  <span className="nl-chip-ar">{cat?.labelAr ?? cat?.label ?? label}</span>
                  {cat?.labelAr && <span className="nl-chip-en">{cat.label}</span>}

                  {!locked && (
                    <button
                      type="button"
                      className="nl-chip-x"
                      aria-label={`Remove ${cat?.label ?? label}`}
                      onClick={() => onChange(selected.filter((c) => c !== label))}
                    ><IconClose width={12} height={12} /></button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {!locked && (
          <div className="nl-catpicker-wrap">
            <button
              type="button"
              className={`nl-selectaudience${selected.length === 0 ? " is-empty" : ""}`}
              aria-haspopup="dialog"
              aria-expanded={picking}
              onClick={() => setPicking((v) => !v)}
            >
              <IconPlus />
              {selected.length === 0 ? "Select categories" : "Add or change categories"}
            </button>
            {picking && (
              <CategoryPicker
                selected={selected}
                catalogue={catalogue}
                onChange={onChange}
                onClose={() => setPicking(false)}
              />
            )}
          </div>
        )}
      </div>

      {selected.length === 0 ? (
        <p className="nl-audience-note is-empty">
          {locked ? "No categories on this newsletter." : "Required — pick at least one."}
        </p>
      ) : (
        <div className="nl-reach">
          {/* Reach needs a count from the member warehouse. The figure here
              was derived from hard-coded sample numbers, which is worse than
              no figure at all — it reads as fact. */}
          <div className="nl-reach-n">
            {selected.length === 1 ? "One category selected" : `${selected.length} categories selected`}
          </div>
          <p className="nl-reach-note">
            {selected.length === 1
              ? <>All of them receive the same {nameOf(selected[0])?.labelAr ?? nameOf(selected[0])?.label ?? selected[0]} articles.</>
              : <>{selected.length} versions — each reader gets their own category's articles.</>}
          </p>
        </div>
      )}
    </section>
  );
}
